/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: EditStatusModal.tsx
 🗓️ Created: 2025-12-10
 🧩 Description:
    Modal to edit extension forwarding settings - compact layout.
 ────────────────────────────────────────────────────────────────*/

import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Settings, Loader2, Save, Phone } from 'lucide-react';
import {
    fetchExtensionStatusDetails,
    updateForwardingSettings,
    ExtensionStatusEntry,
    ExtensionStatusDetails,
} from './api';
import { useNotifications } from '../../contexts/NotificationContext';
import { Toggle } from '../../components/ui/Toggle';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import SelectTimeGroup from '../../components/ui/SelectTimeGroup';
import { FormBox } from '../../components/ui/FormBox';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { fetchTimeGroups } from '../timeGroups/api/timeGroupApi';
import { Button } from '../../components/ui/button';

interface EditStatusModalProps {
    open: boolean;
    onClose: () => void;
    extension: ExtensionStatusEntry;
    onSave: () => void;
}

interface TimeGroupOption {
    id: number;
    name: string;
    description?: string;
}

export default function EditStatusModal({ open, onClose, extension, onSave }: EditStatusModalProps) {
    const { t } = useTranslation();
    const { showError, showSuccess } = useNotifications();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formState, setFormState] = useState<Partial<ExtensionStatusDetails>>({});
    const [timeGroups, setTimeGroups] = useState<TimeGroupOption[]>([]);
    const [loadingTimeGroups, setLoadingTimeGroups] = useState(false);

    // Load time groups
    useEffect(() => {
        const loadTimeGroups = async () => {
            setLoadingTimeGroups(true);
            try {
                const result = await fetchTimeGroups({
                    page: 1,
                    limit: 100,
                    sortBy: 'name',
                    sortOrder: 'asc',
                });
                setTimeGroups(Array.isArray(result.data) ? result.data : []);
            } catch (err) {
                console.error('Failed to load time groups:', err);
            } finally {
                setLoadingTimeGroups(false);
            }
        };
        if (open) {
            loadTimeGroups();
        }
    }, [open]);

    // Load details
    useEffect(() => {
        if (open && extension) {
            setLoading(true);
            setError(null);
            fetchExtensionStatusDetails(extension.uuid || extension.id)
                .then(data => {
                    setFormState(data);
                })
                .catch(err => {
                    setError(err.message || 'Failed to load extension details');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [open, extension]);

    // Update form state helper
    const updateField = useCallback(<K extends keyof ExtensionStatusDetails, SK extends keyof ExtensionStatusDetails[K]>(
        field: K,
        subField: SK,
        value: ExtensionStatusDetails[K][SK]
    ) => {
        setFormState(prev => ({
            ...prev,
            [field]: {
                ...(prev[field] as Record<string, unknown>),
                [subField]: value,
            },
        }));
    }, []);

    // Save handler
    const handleSave = useCallback(async () => {
        if (!extension) return;

        setSaving(true);
        try {
            await updateForwardingSettings(extension.uuid || extension.id, formState);
            showSuccess(t('extensionStatus.saved', 'Settings saved successfully'));
            onSave();
        } catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    }, [extension, formState, showSuccess, showError, t, onSave]);

    // Render a single forwarding row - compact layout
    const renderForwardingRow = (
        label: string,
        tooltip: string,
        enabled: boolean,
        onEnabledChange: (value: boolean) => void,
        timeGroupId?: number | null,
        onTimeGroupChange?: (value: number | null) => void,
        destination?: string,
        onDestinationChange?: (value: string) => void
    ) => {
        const hasTimeGroup = timeGroupId !== undefined && onTimeGroupChange;
        const hasDestination = destination !== undefined && onDestinationChange;

        return (
            <div className="grid grid-cols-[200px_100px_1fr_1fr] gap-x-4 gap-y-1 items-center py-1.5 border-b border-border/30 last:border-b-0">
                <SimpleTooltip content={tooltip} position="top" maxWidth="300px">
                    <Label className="text-left font-medium text-sm truncate">{label}</Label>
                </SimpleTooltip>
                <Toggle
                    value={enabled}
                    onChange={onEnabledChange}
                    hideText={false}
                />
                {hasTimeGroup ? (
                    <SelectTimeGroup
                        value={timeGroupId ?? null}
                        onChange={(value) => onTimeGroupChange(value)}
                        timeGroups={timeGroups}
                        placeholder={loadingTimeGroups ? t('common.loading') : t('extensions.followMe.timeGroup.placeholder', 'Time Group')}
                        disabled={loadingTimeGroups || !enabled}
                    />
                ) : (
                    <div></div>
                )}
                {hasDestination ? (
                    <Input
                        value={destination}
                        onChange={(e) => onDestinationChange(e.target.value)}
                        placeholder={t('extensionStatus.destination', 'Destination')}
                        disabled={!enabled}
                        className={!enabled ? 'opacity-50' : ''}
                    />
                ) : (
                    <div></div>
                )}
            </div>
        );
    };

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 99999 }}>
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative z-10 w-full max-w-5xl mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-border overflow-hidden max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
                    <div className="flex items-center gap-3">
                        <Settings className="h-5 w-5 text-primary" />
                        <h2 className="text-lg font-semibold text-foreground">
                            {t('extensionStatus.editTitle', 'Extension Status')}
                        </h2>
                        <span className="text-sm text-muted-foreground">
                            - {extension.extension} {extension.name && `(${extension.name})`}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        <X className="h-5 w-5 text-muted-foreground" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 text-destructive">
                            {error}
                        </div>
                    ) : !formState ? (
                        <div className="text-center py-12 text-muted-foreground">
                            {t('extensionStatus.noData', 'No data available')}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Call Forwarding Section */}
                            <FormBox
                                title={t('extensionStatus.callForwarding', 'Call Forwarding')}
                                icon={<Phone className="h-5 w-5" />}
                            >
                                {/* Headers */}
                                <div className="grid grid-cols-[200px_100px_1fr_1fr] gap-x-4 gap-y-1 items-center pb-2 mb-2 border-b border-border text-xs font-medium text-muted-foreground uppercase">
                                    <span>{t('common.feature', 'Feature')}</span>
                                    <span>{t('common.enabled', 'On')}</span>
                                    <span>{t('extensionStatus.timeGroup', 'Time Group')}</span>
                                    <span>{t('extensionStatus.destination', 'Destination')}</span>
                                </div>

                                {/* Boss/Secretary */}
                                {renderForwardingRow(
                                    t('extensionStatus.bossSecretary', 'Boss/Secretary'),
                                    t('extensionStatus.bossSecretaryTip', 'Enable Boss/Secretary filtering'),
                                    formState.bossSecretary?.enabled || false,
                                    (v) => updateField('bossSecretary', 'enabled', v),
                                    formState.bossSecretary?.timeGroupId,
                                    (v) => updateField('bossSecretary', 'timeGroupId', v)
                                )}

                                {/* Follow Me */}
                                {renderForwardingRow(
                                    t('extensionStatus.followMe', 'Follow-me'),
                                    t('extensionStatus.followMeTip', 'Ring multiple destinations'),
                                    formState.followMe?.enabled || false,
                                    (v) => updateField('followMe', 'enabled', v),
                                    formState.followMe?.timeGroupId,
                                    (v) => updateField('followMe', 'timeGroupId', v)
                                )}

                                {/* DND */}
                                {renderForwardingRow(
                                    t('extensionStatus.dnd', 'Do Not Disturb'),
                                    t('extensionStatus.dndTip', 'Reject all calls'),
                                    formState.dnd?.enabled || false,
                                    (v) => updateField('dnd', 'enabled', v),
                                    formState.dnd?.timeGroupId,
                                    (v) => updateField('dnd', 'timeGroupId', v)
                                )}

                                {/* Call Completion */}
                                {renderForwardingRow(
                                    t('extensionStatus.callCompletion', 'Call Completion'),
                                    t('extensionStatus.callCompletionTip', 'CCBS/CCNR feature'),
                                    formState.callCompletion?.enabled || false,
                                    (v) => updateField('callCompletion', 'enabled', v),
                                    formState.callCompletion?.timeGroupId,
                                    (v) => updateField('callCompletion', 'timeGroupId', v)
                                )}

                                {/* CFI */}
                                {renderForwardingRow(
                                    t('extensionStatus.cfiLong', 'CFI (Forward All)'),
                                    t('extensionStatus.cfiTip', 'Forward all calls immediately'),
                                    formState.cfi?.enabled || false,
                                    (v) => updateField('cfi', 'enabled', v),
                                    formState.cfi?.timeGroupId,
                                    (v) => updateField('cfi', 'timeGroupId', v),
                                    formState.cfi?.destination || '',
                                    (v) => updateField('cfi', 'destination', v)
                                )}

                                {/* CFB */}
                                {renderForwardingRow(
                                    t('extensionStatus.cfbLong', 'CFB (On Busy)'),
                                    t('extensionStatus.cfbTip', 'Forward when busy'),
                                    formState.cfb?.enabled || false,
                                    (v) => updateField('cfb', 'enabled', v),
                                    formState.cfb?.timeGroupId,
                                    (v) => updateField('cfb', 'timeGroupId', v),
                                    formState.cfb?.destination || '',
                                    (v) => updateField('cfb', 'destination', v)
                                )}

                                {/* CFN */}
                                {renderForwardingRow(
                                    t('extensionStatus.cfnLong', 'CFN (No Answer)'),
                                    t('extensionStatus.cfnTip', 'Forward when no answer'),
                                    formState.cfn?.enabled || false,
                                    (v) => updateField('cfn', 'enabled', v),
                                    formState.cfn?.timeGroupId,
                                    (v) => updateField('cfn', 'timeGroupId', v),
                                    formState.cfn?.destination || '',
                                    (v) => updateField('cfn', 'destination', v)
                                )}

                                {/* CFU */}
                                {renderForwardingRow(
                                    t('extensionStatus.cfuLong', 'CFU (Unreachable)'),
                                    t('extensionStatus.cfuTip', 'Forward when not registered'),
                                    formState.cfu?.enabled || false,
                                    (v) => updateField('cfu', 'enabled', v),
                                    formState.cfu?.timeGroupId,
                                    (v) => updateField('cfu', 'timeGroupId', v),
                                    formState.cfu?.destination || '',
                                    (v) => updateField('cfu', 'destination', v)
                                )}
                            </FormBox>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
                    <Button
                        variant="outline"
                        onClick={onClose}
                    >
                        {t('common.close', 'Close')}
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving || loading}
                    >
                        {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                            <Save className="h-4 w-4 mr-2" />
                        )}
                        {t('common.save', 'Save')}
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    );
}
